// The app's API client and session (tokens in localStorage, see @qrkey/api-client).
import { createApiClient, createStorageTokenStore, type TokenPair } from '@qrkey/api-client';
import { useSyncExternalStore } from 'react';
import { isOfflineSession, sessionHooks } from '../offline/session-core.js';
import { terminalStore } from './terminal.js';

export const tokens = createStorageTokenStore('qrkey.staff.session.v1');

type SessionEndListener = () => void;
const sessionEndListeners = new Set<SessionEndListener>();

export function onSessionEnded(listener: SessionEndListener): () => void {
  sessionEndListeners.add(listener);
  return () => {
    sessionEndListeners.delete(listener);
  };
}

/**
 * A person signed in offline holds a local session the server has never seen: their
 * requests wait for the real one (the PIN they typed is kept in memory and used as soon as
 * the cloud answers, offline/session.ts). Until then the client behaves as if offline.
 */
async function sessionAwareFetch(request: Request): Promise<Response> {
  if (!isOfflineSession(tokens.get())) return fetch(request);
  if (new URL(request.url).pathname.startsWith('/v1/auth/')) {
    // Signing in (roster, PIN): the local session's placeholder token is not sent.
    const headers = new Headers(request.headers);
    headers.delete('authorization');
    return fetch(new Request(request, { headers }));
  }
  if (!(await sessionHooks.upgrade()))
    throw new TypeError('Signed in offline; waiting for the connection');
  const fresh = tokens.get();
  const headers = new Headers(request.headers);
  if (fresh) headers.set('authorization', `Bearer ${fresh.accessToken}`);
  return fetch(new Request(request, { headers }));
}

export const api = createApiClient({
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  tokens,
  fetch: sessionAwareFetch,
  terminalSecret: () => terminalStore.get()?.secret,
  onSessionEnded: () => sessionEndListeners.forEach((l) => l()),
});

export function useSession(): TokenPair | null {
  return useSyncExternalStore(tokens.subscribe, tokens.get, tokens.get);
}
